import { Button, Card, CardContent, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { useCreateCommunityContact } from '@/api/endpoints/contact/useCreateCommunityContact';
import {
  ApiRequest_Create_Contact,
  useCreateContact,
} from '@/api/endpoints/contact/useCreateContact';
import { CreateContactDetailDto, ContactRole as ContactRoleType } from '@/api/models/contact';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';
import { PMPRoutes } from '@/config/routes';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import FormAutocomplete, { Option } from '@/components/form/FormAutocomplete';
import FormSelect from '@/components/form/FormSelect';
import FormTextField from '@/components/form/FormTextField';
import NoPropertyLocation from '@/components/property-manager-portal/NoPropertyLocation';


interface ContactMetadata {
  additional_details?: string;
  [key: string]: unknown;
}

interface ContactFormData
  extends Omit<
    ApiRequest_Create_Contact,
    'details' | 'building_ids' | 'unit_ids' | 'location_ids' | 'role'
  > {
  role: ContactRoleType;
  details: CreateContactDetailDto[];
  building_ids: number[];
  unit_ids: number[];
  location_ids: Option[];
  additional_details?: string;
}

const contactRoleOptions: { label: string; value: ContactRoleType }[] = [
  { label: 'Tenant', value: 'TENANT' },
  { label: 'Landlord', value: 'LANDLORD' },
  { label: 'Property Manager', value: 'PROPERTY_MANAGER' },
  { label: 'Owner Representative', value: 'OWNER_REPRESENTATIVE' },
  { label: 'Cleaning Service', value: 'CLEANING_SERVICE' },
  { label: 'HVAC Contractor', value: 'HVAC_CONTRACTOR' },
  { label: 'Electric Provider', value: 'ELECTRIC_PROVIDER' },
  { label: 'Water Provider', value: 'WATER_PROVIDER' },
  { label: 'Wastewater Service', value: 'WASTEWATER_SERVICE' },
  { label: 'Propane Supplier', value: 'PROPANE_SUPPLIER' },
  { label: 'Trash Removal', value: 'TRASH_REMOVAL' },
  { label: 'Tree Service', value: 'TREE_SERVICE' },
  { label: 'Pest Control', value: 'PEST_CONTROL' },
  { label: 'Pool Service', value: 'POOL_SERVICE' },
  { label: 'Lawn Care', value: 'LAWN_CARE' },
  { label: 'Landscaping Vendor', value: 'LANDSCAPING_VENDOR' },
  { label: 'Security Provider', value: 'SECURITY_PROVIDER' },
  { label: 'Sound System Vendor', value: 'SOUND_SYSTEM_VENDOR' },
  { label: 'Water Cooler Vendor', value: 'WATER_COOLER_VENDOR' },
  { label: 'Tax Authority', value: 'TAX_AUTHORITY' },
  { label: 'Insurance Agent', value: 'INSURANCE_AGENT' },
  { label: 'Legal Representative', value: 'LEGAL_REPRESENTATIVE' },
  { label: 'Vendor', value: 'VENDOR' },
  { label: 'Contractor', value: 'CONTRACTOR' },
  { label: 'Service Provider', value: 'SERVICE_PROVIDER' },
  { label: 'Emergency Contact', value: 'EMERGENCY_CONTACT' },
  { label: 'Government', value: 'GOVERNMENT' },
  { label: 'Utility', value: 'UTILITY' },
  { label: 'Other', value: 'OTHER' },
];

const contactDetailTypeOptions = [
  { label: 'Email', value: 'email' },
  { label: 'Phone', value: 'phone' },
];

const contactDetailLabelOptions = [
  { label: 'Primary', value: 'primary' },
  { label: 'Work', value: 'work' },
  { label: 'Mobile', value: 'mobile' },
  { label: 'Home', value: 'home' },
  { label: 'Other', value: 'other' },
];

const ContactCreatePage: React.FC = () => {
  const { propertyLocations, selectedPropertyLocation } = usePropertyManager();
  const history = useHistory();
  const location = useLocation();
  const [formError, setFormError] = useState<Error | null>(null);

  // Check if we're creating a contact for a community
  const urlParams = new URLSearchParams(location.search);
  const communityId = urlParams.get('community_id');
  const isCommunityContact = !!communityId;

  const locationOptions = propertyLocations.map(loc => ({
    label: loc.name,
    value: loc.id,
  }));

  const defaultLocationOptions = selectedPropertyLocation
    ? [{ label: selectedPropertyLocation.name, value: selectedPropertyLocation.id }]
    : [];

  const form = useForm<ContactFormData>({
    defaultValues: {
      name: '',
      role: 'TENANT' as ContactRoleType,
      details: [{ type: 'email' as const, value: '', is_primary: true }],
      building_ids: [],
      unit_ids: [],
      location_ids: defaultLocationOptions,
      additional_details: '',
    },
    mode: 'onChange',
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = form;

  const {
    fields: detailFields,
    append: appendDetail,
    remove: removeDetail,
  } = useFieldArray({
    control,
    name: 'details',
  });

  const createContactMutation = useCreateContact({
    onSuccess: () => {
      history.push(PMPRoutes.contactList);
    },
    onError: error => {
      setFormError(error);
    },
  });

  const createCommunityContactMutation = useCreateCommunityContact();

  const handleBack = () => {
    if (isCommunityContact && communityId) {
      history.push(`${PMPRoutes.communityView.replace(':id', communityId)}?tab=contacts`);
    } else {
      history.push(PMPRoutes.contactList);
    }
  };

  const onSubmit = useCallback(
    (data: ContactFormData) => {
      setFormError(null);
      clearErrors('location_ids');

      // Filter out empty contact details and ensure proper types
      const filteredDetails = data.details
        .filter(detail => detail.value.trim() !== '')
        .map(detail => ({
          ...detail,
          type: detail.type as 'email' | 'phone' | 'other'
        }));

      // Prepare metadata with additional details
      const metadata: ContactMetadata | undefined = data.additional_details?.trim()
        ? { additional_details: data.additional_details.trim() }
        : undefined;

      if (isCommunityContact && communityId) {
        // For community contacts, we don't need location_ids
        const { location_ids: _, additional_details: __, ...apiData } = data;

        return createCommunityContactMutation.mutateAsync({
          communityId: parseInt(communityId, 10),
          data: {
            ...apiData,
            metadata,
            details: filteredDetails.length > 0 ? filteredDetails : undefined,
          },
        }).then(() => {
          history.push(`${PMPRoutes.communityView.replace(':id', communityId)}?tab=contacts`);
        }).catch(error => {
          setFormError(error);
          throw error;
        });
      }
      
      // Convert Option objects to numbers for API
      const locationIds = data.location_ids.map(option => option.value as number);

      // Validate that at least one location is selected
      if (!locationIds || locationIds.length === 0) {
        setError('location_ids', {
          type: 'required',
          message: 'At least one location is required',
        });
        return;
      }

      // Destructure to exclude location_ids from spread
      const { location_ids: _, additional_details: __, ...apiData } = data;

      return createContactMutation.mutateAsync({
        ...apiData,
        metadata,
        details: filteredDetails.length > 0 ? filteredDetails : undefined,
        building_ids: data.building_ids.length > 0 ? data.building_ids : undefined,
        unit_ids: data.unit_ids.length > 0 ? data.unit_ids : undefined,
        location_ids: locationIds,
      });
    },
    [
      createContactMutation, 
      createCommunityContactMutation, 
      setError, 
      clearErrors, 
      isCommunityContact, 
      communityId,
      history
    ]
  );

  const handleAddDetail = () => {
    appendDetail({ type: 'email' as const, value: '', is_primary: false });
  };

  if (!isCommunityContact && propertyLocations.length === 0) {
    return (
      <MuiPageWrapper>
        <NoPropertyLocation
          title="Add Property Location First"
          description="Before you can add contacts, you need to set up at least one property location. This helps organize your contacts by property."
          buttonText="Add Property Location"
        />
      </MuiPageWrapper>
    );
  }

  return (
    <MuiPageWrapper>
      <Stack direction="column" spacing={3} sx={{ flex: '1 1 100%' }}>
        <DashboardHeader title={isCommunityContact ? 'Add Community Contact' : 'Add New Contact'}>
          <Button
            color="secondary"
            variant="outlined"
            size="medium"
            startIcon={<ArrowLeftIcon />}
            onClick={handleBack}
          >
            {isCommunityContact ? 'Back to Community' : 'Back to Contacts'}
          </Button>
        </DashboardHeader>

        <Typography variant="body3">
          {isCommunityContact 
            ? 'Create a new contact for this community.'
            : 'Create a new contact and associate it with one or more property locations.'}
        </Typography>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={4}>
              {/* Basic Information */}
              <Stack spacing={3}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Basic Information
                </Typography>

                <FormTextField
                  label="Contact Name"
                  placeholder="Enter contact name"
                  required
                  control={control}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  {...register('name', {
                    required: 'Contact name is required',
                  })}
                  size="small"
                  sx={{ maxWidth: 400 }}
                />

                <FormSelect
                  label="Role"
                  placeholder="Select contact role"
                  required
                  control={control}
                  options={contactRoleOptions}
                  error={!!errors.role}
                  helperText={errors.role?.message}
                  {...register('role', {
                    required: 'Contact role is required',
                  })}
                  size="small"
                  sx={{ maxWidth: 400 }}
                />

                {!isCommunityContact && (
                  <FormAutocomplete
                    name="location_ids"
                    label="Property Locations"
                    placeholder="Select property locations"
                    required
                    control={control}
                    options={locationOptions}
                    multiple
                    filterSelectedOptions
                    error={!!errors.location_ids}
                    helperText={
                      errors.location_ids?.message ||
                      'Select one or more locations to associate with this contact'
                    }
                    size="small"
                    sx={{ maxWidth: 600 }}
                  />
                )}

                <FormTextField
                  label="Additional Details"
                  placeholder="Enter any additional information about this contact"
                  control={control}
                  error={!!errors.additional_details}
                  helperText={errors.additional_details?.message}
                  {...register('additional_details')}
                  multiline
                  rows={3}
                  size="small"
                  sx={{ maxWidth: 600 }}
                />
              </Stack>

              {/* Contact Details */}
              <Stack spacing={3}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Contact Details
                </Typography>

                {detailFields.map((field, index) => (
                  <Card key={field.id} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent sx={{ pb: 2 }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems="flex-start"
                        sx={{ width: '100%' }}
                      >
                        <FormSelect
                          label="Type"
                          control={control}
                          options={contactDetailTypeOptions}
                          {...register(`details.${index}.type`)}
                          sx={{
                            minWidth: { xs: '100%', sm: 120 },
                            maxWidth: { xs: '100%', sm: 150 },
                          }}
                          size="small"
                        />

                        <FormTextField
                          label="Value"
                          placeholder={
                            watch(`details.${index}.type`) === 'email'
                              ? 'Enter email address'
                              : 'Enter phone number'
                          }
                          control={control}
                          error={!!errors.details?.[index]?.value}
                          helperText={errors.details?.[index]?.value?.message}
                          {...register(`details.${index}.value`, {
                            required: 'Value is required',
                            pattern:
                              watch(`details.${index}.type`) === 'email'
                                ? {
                                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                  message: 'Invalid email address',
                                }
                                : undefined,
                          })}
                          sx={{
                            flex: 1,
                            minWidth: { xs: '100%', sm: 200 },
                          }}
                          size="small"
                        />

                        <FormSelect
                          label="Label"
                          control={control}
                          options={contactDetailLabelOptions}
                          {...register(`details.${index}.label`)}
                          sx={{
                            minWidth: { xs: '100%', sm: 120 },
                            maxWidth: { xs: '100%', sm: 150 },
                          }}
                          size="small"
                        />

                        {detailFields.length > 1 && (
                          <IconButton
                            onClick={() => removeDetail(index)}
                            color="error"
                            sx={{
                              mt: { xs: 0, sm: 2 },
                              alignSelf: { xs: 'center', sm: 'flex-start' },
                            }}
                            size="small"
                          >
                            <Trash01Icon />
                          </IconButton>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}

                <Button
                  variant="outlined"
                  startIcon={<PlusIcon />}
                  onClick={handleAddDetail}
                  size="small"
                  sx={{
                    alignSelf: 'flex-start',
                    mt: 1,
                  }}
                >
                  Add Contact Detail
                </Button>
              </Stack>

              {/* Error Display */}
              {formError && (
                <Typography color="error" variant="body2">
                  {formError.message}
                </Typography>
              )}

              {/* Submit Button */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={handleBack} disabled={isSubmitting}>
                  Cancel
                </Button>
                <LoadingButton loading={isSubmitting} variant="contained" type="submit">
                  Create Contact
                </LoadingButton>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </MuiPageWrapper>
  );
};

export default ContactCreatePage;
